import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import radiusLogo from "@/assets/radius-logo.png";

export const PublicHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <nav className="container flex h-16 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex-1 flex items-center space-x-2">
          <img src={radiusLogo} alt="Radius" className="h-8 w-8 rounded-lg" />
          <span className="text-xl font-bold">Radius</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex md:items-center md:gap-6">
          <Link to="/features" className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors">
            Features
          </Link>
          <Link to="/pricing" className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors">
            Pricing
          </Link>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors">
                Solutions
                <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              <DropdownMenuItem asChild>
                <Link to="/for-clients" className="cursor-pointer">For Buyers</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/for-agencies" className="cursor-pointer">For Agencies</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/for-procurement" className="cursor-pointer">For Procurement</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/for-vendors" className="cursor-pointer">For Vendors</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/supplier-diversity" className="cursor-pointer">Supplier Diversity</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors">
                How It Works
                <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              <DropdownMenuItem asChild>
                <Link to="/how-it-works/clients" className="cursor-pointer">For Buyers</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/how-it-works/vendors" className="cursor-pointer">For Vendors</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/verification" className="cursor-pointer">How Verification Works</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/insights" className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors">
            Insights
          </Link>

          <Link to="/contact" className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors">
            Contact
          </Link>
        </div>

        {/* Desktop Auth Buttons */}
        <div className="hidden md:flex md:items-center md:gap-3 flex-1 justify-end">
          <Button variant="ghost" asChild>
            <Link to="/auth/login">Login</Link>
          </Button>
          <Button asChild>
            <Link to="/auth/signup">Get Started</Link>
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t bg-background">
          <div className="container py-4 space-y-4">
            <Link
              to="/features"
              className="block text-sm font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Features
            </Link>
            <Link
              to="/pricing"
              className="block text-sm font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </Link>
            <Link
              to="/for-clients"
              className="block text-sm font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              For Buyers
            </Link>
            <Link
              to="/for-agencies"
              className="block text-sm font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              For Agencies
            </Link>
            <Link
              to="/for-procurement"
              className="block text-sm font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              For Procurement
            </Link>
            <Link
              to="/for-vendors"
              className="block text-sm font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              For Vendors
            </Link>
            <Link
              to="/supplier-diversity"
              className="block text-sm font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Supplier Diversity
            </Link>
            <Link
              to="/how-it-works/clients"
              className="block text-sm font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              How It Works - Buyers
            </Link>
            <Link
              to="/how-it-works/vendors"
              className="block text-sm font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              How It Works - Vendors
            </Link>
            <Link
              to="/verification"
              className="block text-sm font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              How Verification Works
            </Link>
            <Link
              to="/insights"
              className="block text-sm font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Insights
            </Link>
            <Link
              to="/contact"
              className="block text-sm font-medium py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact
            </Link>
            <div className="pt-4 space-y-2 border-t">
              <Button variant="outline" className="w-full" asChild>
                <Link to="/auth/login" onClick={() => setMobileMenuOpen(false)}>
                  Login
                </Link>
              </Button>
              <Button className="w-full" asChild>
                <Link to="/auth/signup" onClick={() => setMobileMenuOpen(false)}>
                  Get Started
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
